import type { HttpRequestInputContract } from "@/types/httpRequestTool"

export const HTTP_INPUT_TYPES = [
  "string",
  "number",
  "integer",
  "boolean",
  "object",
  "array",
] as const
export interface HttpAgentInput {
  key: string
  path: string
  instruction: string
  wholeValue: boolean
}

// Keep generated keys in sync with collect_http_request_agent_placeholders.
export const collectHttpAgentInputs = (template: unknown): HttpAgentInput[] => {
  const fields: HttpAgentInput[] = []
  const used = new Set<string>()
  const walk = (value: unknown, path: string) => {
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, `${path}[${index}]`))
    } else if (value !== null && typeof value === "object") {
      Object.entries(value).forEach(([key, item]) =>
        walk(item, path ? `${path}.${key}` : key)
      )
    } else if (typeof value === "string") {
      const matches = [...value.matchAll(/\{\{\s*agent\[(.*?)\]\s*\}\}/g)]
      matches.forEach((match, index) => {
        let base =
          (path || "body")
            .replace(/[^a-zA-Z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "")
            .toLowerCase() || "body"
        if (matches.length > 1) base += `_${index + 1}`
        let key = base
        for (let suffix = 2; used.has(key); suffix++) key = `${base}_${suffix}`
        used.add(key)
        fields.push({
          key,
          path: path || "body",
          instruction: match[1].trim(),
          wholeValue: match[0] === value,
        })
      })
    }
  }
  walk(template, "")
  return fields
}

export const isHttpInputContract = (
  value: unknown
): value is HttpRequestInputContract => {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false
  const contract = value as HttpRequestInputContract
  if (
    Object.keys(value).some(
      (key) => !["type", "format", "jsonType"].includes(key)
    )
  )
    return false
  if (
    !HTTP_INPUT_TYPES.includes(
      contract.type === undefined ? "string" : contract.type
    )
  )
    return false
  if (
    !["text", "json"].includes(
      contract.format === undefined ? "text" : contract.format
    )
  )
    return false
  if (
    !["any", "object", "array"].includes(
      contract.jsonType === undefined ? "any" : contract.jsonType
    )
  )
    return false
  if (contract.format === "json")
    return (contract.type === undefined ? "string" : contract.type) === "string"
  return (contract.jsonType === undefined ? "any" : contract.jsonType) === "any"
}

export const getHttpInputContractErrors = (
  template: unknown,
  contracts: Record<string, HttpRequestInputContract> = {}
) => {
  const fields = collectHttpAgentInputs(template)
  const errors: Array<{ key: string; reason: "unused" | "mixed" | "invalid" }> =
    []
  for (const [key, contract] of Object.entries(contracts)) {
    const field = fields.find((field) => field.key === key)
    if (!field) errors.push({ key, reason: "unused" })
    else if (!isHttpInputContract(contract))
      errors.push({ key, reason: "invalid" })
    else if (
      !field.wholeValue &&
      ((contract.type === undefined ? "string" : contract.type) !== "string" ||
        contract.format === "json")
    ) {
      errors.push({ key, reason: "mixed" })
    }
  }
  return errors
}
